import { useState, useEffect } from "react";

export interface GeoInfo {
  countryCode: string;
  countryName: string;
  loading: boolean;
}

const CACHE_KEY = "aitools_geo_v1";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// UAE fallback — matches the app's primary market
const FALLBACK: Omit<GeoInfo, "loading"> = {
  countryCode: "AE",
  countryName: "United Arab Emirates",
};

export function useGeoLocation(): GeoInfo {
  const [geo, setGeo] = useState<GeoInfo>({ ...FALLBACK, loading: true });

  useEffect(() => {
    // Check localStorage cache first — avoids API call on every visit
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts < CACHE_TTL) {
          setGeo({ ...data, loading: false });
          return;
        }
      }
    } catch {}

    // Silent IP-based lookup — no browser permission needed
    fetch("https://ipapi.co/json/")
      .then((r) => r.json())
      .then((d) => {
        const data = {
          countryCode: (d.country_code as string) || FALLBACK.countryCode,
          countryName: (d.country_name as string) || FALLBACK.countryName,
        };
        try {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ data, ts: Date.now() })
          );
        } catch {}
        setGeo({ ...data, loading: false });
      })
      .catch(() => {
        // Silently fall back — user never sees an error for this
        setGeo({ ...FALLBACK, loading: false });
      });
  }, []);

  return geo;
}
